import type { ApiError } from "@/api/client";
import { updateFrame, type Frame } from "@/api/spaces";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import {
  rememberLocalFrameEdit,
  type ComparableContent,
} from "@/stores/frame-edit-guard";
import { applyFramePatch, refusesCardContentWrite } from "@/lib/frame-ingest";
import { translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";

type Content = Record<string, unknown>;

interface QueuedFrameContentSaveOptions {
  frameId: string;
  initialContent: Content;
  /** The failure toast's title, read when the toast is shown. */
  errorTitle: () => string;
  projectId?: string;
  /**
   * Called with the server's current frame when a save is refused with 409
   * (a Doc changed by an agent after this client loaded it). The queue is
   * cleared and no error toast is shown; the caller owns the recovery.
   */
  onConflict?: (frame: Frame) => void;
  /** How the echo guard compares this frame's content (see frame-edit-guard). */
  comparable?: ComparableContent;
}

function conflictFrame(caught: unknown): Frame | null {
  const error = caught as Partial<ApiError> | null;
  if (error?.status !== 409) return null;
  const frame = error.data?.data;
  return typeof frame === "object" && frame !== null ? (frame as Frame) : null;
}

/** Serialize full-content frame patches so later leaf edits include earlier ones. */
export function createQueuedFrameContentSave({
  frameId,
  initialContent,
  errorTitle,
  projectId: requestedProjectId,
  onConflict,
  comparable,
}: QueuedFrameContentSaveOptions) {
  const projectId =
    requestedProjectId ?? useProjectStore.getState().currentProject?.id;
  let draft = initialContent;
  let queue = Promise.resolve();
  let lastError: unknown = null;
  let flushInFlight: Promise<boolean> | null = null;
  let revision = 0;
  /** Bumped by a conflict so saves queued before it are dropped. */
  let generation = 0;

  function handleConflict(frame: Frame) {
    generation += 1;
    lastError = null;
    draft = frame.content;
    applyFramePatch(frameId, frame, { origin: "server" });
    onConflict?.(frame);
  }

  function enqueue(content: Content, contentRevision: number) {
    const queuedGeneration = generation;
    queue = queue.then(async () => {
      if (queuedGeneration !== generation) return;
      try {
        if (!projectId) {
          throw new Error("Project context is unavailable.");
        }
        const updatedFrame = await updateFrame(projectId, frameId, {
          content,
        });
        if (contentRevision === revision) {
          applyFramePatch(frameId, updatedFrame, { origin: "server" });
        }
        lastError = null;
      } catch (caught) {
        const conflict = onConflict ? conflictFrame(caught) : null;
        if (conflict) {
          handleConflict(conflict);
          return;
        }
        lastError = caught;
        if (contentRevision === revision) {
          applyFramePatch(frameId, { content: draft });
        }
        useNotificationStore.getState().addSystemToast({
          id: `frame-edit-error-${frameId}`,
          title: errorTitle(),
          description: translate(framesSharedMessages, "saveEditsRetry"),
          level: "error",
        });
      }
    });
  }

  return {
    reset(content: Content) {
      draft = content;
      lastError = null;
      revision += 1;
    },
    update(updater: (content: Content) => Content) {
      // A card-only frame holds no content to edit (Doc saves come here too).
      if (refusesCardContentWrite(frameId)) return;
      // The store holds earlier optimistic edits plus any agent update that
      // landed while editing; patching the private draft would revert those.
      const current = useFramesStore.getState().framesById[frameId]?.content;
      draft = updater(current ?? draft);
      revision += 1;
      // This newer snapshot is queued now, so a failed earlier one needs no retry.
      lastError = null;
      rememberLocalFrameEdit(frameId, { content: draft }, comparable);
      applyFramePatch(frameId, { content: draft });
      enqueue(draft, revision);
    },
    flush() {
      if (flushInFlight) return flushInFlight;
      flushInFlight = (async () => {
        if (lastError) {
          lastError = null;
          enqueue(draft, revision);
        }
        let observedQueue = queue;
        await observedQueue;
        while (observedQueue !== queue) {
          observedQueue = queue;
          await observedQueue;
        }
        return !lastError;
      })().finally(() => {
        flushInFlight = null;
      });
      return flushInFlight;
    },
  };
}
